import { NavigateFunction } from 'react-router-dom'
import { createLogger } from '@/utils/logger'

const log = createLogger('Commands')

/**
 * UF-5: focus a page's primary search box after navigation.
 *
 * The legacy `[data-search-input]` attribute lived in the now-disabled
 * QuickNavSidebar and no longer exists in the DOM, which made the old
 * `querySelector('[data-search-input]')` focus calls silent no-ops. This picks
 * the first live, visible, search-style input on the current page, skipping the
 * command palette's own input. Returns true if an input was focused.
 */
function focusPageSearchInput(): boolean {
  const candidates = Array.from(
    document.querySelectorAll<HTMLInputElement>(
      '[data-search-input], input[type="search"], input[placeholder*="earch"], input[aria-label*="earch"]'
    )
  )
  const target = candidates.find(
    (el) =>
      !el.closest('.command-palette') && el.offsetParent !== null && !el.disabled
  )
  if (target) {
    target.focus()
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return true
  }
  return false
}

export type CommandCategory =
  | 'Navigation'
  | 'Actions'
  | 'Search'
  | 'Settings'
  | 'Help'
  // F8: live entity-match groups use dynamic, terminology-aware headings
  // (e.g. "Failure Modes" / "Hazards"), so allow arbitrary strings while
  // keeping literal-completion for the built-in categories.
  | (string & {})

export interface Command {
  id: string
  label: string
  description?: string
  keywords?: string[] // For fuzzy search
  shortcut?: string // Visual hint (e.g., "Ctrl+K")
  category: CommandCategory
  icon?: string // Emoji or icon name
  execute: () => void
}

/**
 * Create commands registry with navigation and actions
 *
 * @param navigate - React Router navigate function
 * @param actions - Object containing action handlers
 */
export function createCommandsRegistry(
  navigate: NavigateFunction,
  actions: {
    openCommandPalette?: () => void
    openShortcutsHelp?: () => void
    toggleTheme?: () => void
    // TODO: Implement PDF export functionality
    exportToPDF?: () => void
    // TODO: Implement Excel export functionality
    exportToExcel?: () => void
    createComponent?: () => void
    createFailureMode?: () => void
    focusSearch?: () => void
  } = {}
): Command[] {
  const commands: Command[] = [
    // Navigation Commands - Core Pages
    {
      id: 'nav-dashboard',
      label: 'Go to Dashboard',
      description: 'View executive dashboard with KPIs and metrics',
      keywords: ['dashboard', 'home', 'overview', 'kpi', 'metrics'],
      shortcut: 'g → d',
      category: 'Navigation',
      icon: '📊',
      execute: () => navigate('/'),
    },
    {
      id: 'nav-components',
      label: 'Go to Components',
      description: 'Manage component hierarchy and details',
      keywords: ['components', 'parts', 'hierarchy', 'tree'],
      shortcut: 'g → c',
      category: 'Navigation',
      icon: '🔧',
      execute: () => navigate('/components'),
    },
    {
      id: 'nav-cause-effect',
      label: 'Go to Failure Modes',
      description: 'FMEA analysis with failure modes and root causes',
      keywords: ['fmea', 'cause', 'effect', 'failure', 'modes', '5m'],
      shortcut: 'g → f',
      category: 'Navigation',
      icon: '🔍',
      execute: () => navigate('/cause-effect'),
    },
    {
      id: 'nav-failures',
      label: 'Go to Failures',
      description: 'View and manage failure modes',
      keywords: ['failures', 'failure modes', 'fmea', 'risks'],
      category: 'Navigation',
      icon: '⚡',
      execute: () => navigate('/cause-effect'),
    },
    {
      id: 'nav-risk-assessment',
      label: 'Go to Risk Assessment',
      description: 'RPN dashboard, risk matrix, and mitigation tracking',
      keywords: ['risk', 'assessment', 'rpn', 'severity', 'mitigation'],
      shortcut: 'g → r',
      category: 'Navigation',
      icon: '⚠️',
      execute: () => navigate('/risk-assessment'),
    },
    {
      id: 'nav-risk-dashboard',
      label: 'Go to Risk Dashboard',
      description: 'Advanced risk visualization and analytics',
      keywords: ['risk', 'dashboard', 'analytics', 'rpn', 'charts'],
      category: 'Navigation',
      icon: '📈',
      execute: () => navigate('/risk-dashboard'),
    },
    {
      id: 'nav-mitigations',
      label: 'Go to Mitigations',
      description: 'Manage mitigation actions and tracking',
      keywords: ['mitigations', 'actions', 'risk reduction', 'controls'],
      category: 'Navigation',
      icon: '🛡️',
      execute: () => navigate('/mitigations'),
    },
    {
      id: 'nav-system-states',
      label: 'Go to System States',
      description: 'Manage operational states for state-dependent risk',
      keywords: ['system states', 'states', 'operational modes', 'state-dependent risk'],
      category: 'Navigation',
      icon: '🔄',
      execute: () => navigate('/system-states'),
    },
    {
      id: 'nav-visualizations',
      label: 'Go to Visualizations',
      description: 'D3.js visualizations: fishbone, tree, network graphs',
      keywords: ['visualizations', 'charts', 'graphs', 'fishbone', 'd3'],
      shortcut: 'g → v',
      category: 'Navigation',
      icon: '📊',
      execute: () => navigate('/visualizations'),
    },

    // Requirements & Traceability
    {
      id: 'nav-requirements',
      label: 'Go to Requirements',
      description: 'Manage requirements and traceability',
      keywords: ['requirements', 'specs', 'traceability'],
      category: 'Navigation',
      icon: '📝',
      execute: () => navigate('/requirements'),
    },
    {
      id: 'nav-test-plans',
      label: 'Go to Test Plans',
      description: 'FAT/SAT verification test plans and coverage',
      keywords: ['test', 'test plans', 'verification', 'fat', 'sat', 'acceptance'],
      category: 'Navigation',
      icon: '🧪',
      execute: () => navigate('/test-plans'),
    },
    {
      id: 'nav-coverage-matrix',
      label: 'Go to Coverage Matrix',
      description: 'View requirements coverage matrix',
      keywords: ['coverage', 'matrix', 'traceability', 'requirements'],
      category: 'Navigation',
      icon: '🎯',
      execute: () => navigate('/coverage-matrix'),
    },
    {
      id: 'nav-traceability',
      label: 'Go to Traceability Report',
      description: 'Generate traceability reports',
      keywords: ['traceability', 'report', 'requirements', 'coverage'],
      category: 'Navigation',
      icon: '📄',
      execute: () => navigate('/traceability-report'),
    },

    {
      id: 'nav-error-paths',
      label: 'Go to Error Paths',
      description: 'Analyze error paths and failure cascades',
      keywords: ['error', 'paths', 'failures', 'cascades'],
      category: 'Navigation',
      icon: '🔀',
      execute: () => navigate('/error-paths'),
    },
    {
      id: 'nav-interfaces',
      label: 'Go to Interfaces',
      description: 'Interface / ICD register of all component connections',
      keywords: ['interfaces', 'icd', 'connections', 'register', 'links'],
      category: 'Navigation',
      icon: '🔌',
      execute: () => navigate('/interfaces'),
    },

    // Design & Change Management
    {
      id: 'nav-baselines',
      label: 'Go to Baselines',
      description: 'Manage project baselines and versions',
      keywords: ['baselines', 'versions', 'snapshots'],
      category: 'Navigation',
      icon: '📌',
      execute: () => navigate('/baselines'),
    },

    // Templates & Analysis
    {
      id: 'nav-templates',
      label: 'Go to Template Manager',
      description: 'Manage FMEA templates',
      keywords: ['templates', 'manager', 'fmea'],
      category: 'Navigation',
      icon: '📑',
      execute: () => navigate('/templates'),
    },
    {
      id: 'nav-template-library',
      label: 'Go to Template Library',
      description: 'Browse template library',
      keywords: ['template', 'library', 'browse', 'catalog'],
      category: 'Navigation',
      icon: '📚',
      execute: () => navigate('/template-library'),
    },

    // Data & Reports
    {
      id: 'nav-import',
      label: 'Go to Import Wizard',
      description: 'Import data from Excel or CSV',
      keywords: ['import', 'wizard', 'excel', 'csv', 'upload'],
      category: 'Navigation',
      icon: '📥',
      execute: () => navigate('/import'),
    },
    {
      id: 'nav-completeness',
      label: 'Go to Completeness Dashboard',
      description: 'View project completeness metrics',
      keywords: ['completeness', 'dashboard', 'metrics', 'coverage'],
      category: 'Navigation',
      icon: '✅',
      execute: () => navigate('/completeness'),
    },
    {
      id: 'nav-audit',
      label: 'Go to Audit Trail',
      description: 'View audit log and change history',
      keywords: ['audit', 'trail', 'log', 'history', 'changes'],
      category: 'Navigation',
      icon: '📜',
      execute: () => navigate('/audit'),
    },
    {
      id: 'nav-notifications',
      label: 'Go to Notifications',
      description: 'View system notifications',
      keywords: ['notifications', 'alerts', 'messages'],
      category: 'Navigation',
      icon: '🔔',
      execute: () => navigate('/notifications'),
    },

    // Newly-surfaced pages (IA restructure) — keep Cmd+K in sync with sidebar
    {
      id: 'nav-mission',
      label: 'Go to Mission',
      description: 'Define the project mission and scope',
      keywords: ['mission', 'scope', 'project', 'define'],
      category: 'Navigation',
      icon: '🎯',
      execute: () => navigate('/mission'),
    },
    {
      id: 'nav-risk-review',
      label: 'Go to Risk Review',
      description: 'Consolidated risk review: dashboard, matrix, and completeness',
      keywords: ['risk', 'review', 'dashboard', 'matrix', 'completeness', 'overview'],
      category: 'Navigation',
      icon: '📊',
      execute: () => navigate('/risk-review'),
    },
    {
      id: 'nav-workshop-report',
      label: 'Go to Workshop Summary',
      description: 'Workshop summary report',
      keywords: ['workshop', 'summary', 'report'],
      category: 'Navigation',
      icon: '📋',
      execute: () => navigate('/workshop-report'),
    },
    {
      id: 'nav-stress-library',
      label: 'Go to Stress Library',
      description: 'Manage the environmental / operational stress library',
      keywords: ['stress', 'library', 'environment', 'loads'],
      category: 'Navigation',
      icon: '🏋️',
      execute: () => navigate('/stress-library'),
    },
    {
      id: 'nav-failure-mode-templates',
      label: 'Go to Failure Mode Templates',
      description: 'Browse reusable failure mode templates',
      keywords: ['failure', 'mode', 'templates', 'library', 'hazard'],
      category: 'Navigation',
      icon: '🗂️',
      execute: () => navigate('/failure-mode-templates'),
    },

    // Action Commands
    {
      id: 'action-create-component',
      label: 'Create Component',
      description: 'Add a new component to the hierarchy',
      keywords: ['create', 'new', 'component', 'add'],
      shortcut: 'n',
      category: 'Actions',
      icon: '➕',
      execute: () => {
        if (actions.createComponent) {
          actions.createComponent()
        } else {
          log.warn(
            'Create Component action not implemented - please pass createComponent handler to createCommandsRegistry'
          )
        }
      },
    },
    {
      id: 'action-create-failure',
      label: 'Create Failure Mode',
      description: 'Add a new failure mode to FMEA',
      keywords: ['create', 'new', 'failure', 'mode', 'fmea'],
      category: 'Actions',
      icon: '⚡',
      execute: () => {
        if (actions.createFailureMode) {
          actions.createFailureMode()
        } else {
          log.warn(
            'Create Failure Mode action not implemented - please pass createFailureMode handler to createCommandsRegistry'
          )
        }
      },
    },
    {
      id: 'action-export-pdf',
      label: 'Export to PDF',
      description: 'Generate PDF report of current view',
      keywords: ['export', 'pdf', 'report', 'download'],
      category: 'Actions',
      icon: '📄',
      execute: () => {
        // TODO: Implement PDF export functionality
        // See Phase 12 implementation for PDF export using jsPDF
        if (actions.exportToPDF) {
          actions.exportToPDF()
        } else {
          log.warn(
            'PDF Export not yet implemented - see Phase 12 requirements for jsPDF integration'
          )
        }
      },
    },
    {
      id: 'action-export-excel',
      label: 'Export to Excel',
      description: 'Download data as Excel spreadsheet',
      keywords: ['export', 'excel', 'spreadsheet', 'download', 'csv'],
      category: 'Actions',
      icon: '📊',
      execute: () => {
        // TODO: Implement Excel export functionality
        // See Phase 12 implementation for Excel export using xlsx library
        if (actions.exportToExcel) {
          actions.exportToExcel()
        } else {
          log.warn(
            'Excel Export not yet implemented - see Phase 12 requirements for xlsx library integration'
          )
        }
      },
    },

    // Search Commands
    {
      id: 'search-focus',
      label: 'Focus Search',
      description: 'Jump to search bar',
      keywords: ['search', 'find', 'filter'],
      shortcut: '/',
      category: 'Search',
      icon: '🔎',
      execute: () => {
        if (actions.focusSearch) {
          actions.focusSearch()
        } else {
          log.warn(
            'Focus Search action not implemented - please pass focusSearch handler to createCommandsRegistry'
          )
        }
      },
    },
    {
      id: 'search-components',
      label: 'Find Component',
      description: 'Search for components by name',
      keywords: ['find', 'search', 'component', 'parts'],
      category: 'Search',
      icon: '🔍',
      execute: () => {
        navigate('/components')
        // Focus search after navigation. UF-5: `[data-search-input]` no longer
        // exists in the DOM, so fall back to any live search-style input on the
        // destination page (skipping the command palette's own input).
        setTimeout(() => {
          focusPageSearchInput()
        }, 100)
      },
    },
    {
      id: 'search-failures',
      label: 'Find Failure',
      description: 'Search for failure modes',
      keywords: ['find', 'search', 'failure', 'mode', 'fmea'],
      category: 'Search',
      icon: '🔍',
      execute: () => {
        navigate('/cause-effect')
        setTimeout(() => {
          focusPageSearchInput()
        }, 100)
      },
    },

    // Settings Commands
    {
      id: 'settings-theme',
      label: 'Toggle Dark Mode',
      description: 'Switch between light and dark theme',
      keywords: ['theme', 'dark', 'light', 'mode'],
      category: 'Settings',
      icon: '🌙',
      execute: () => {
        if (actions.toggleTheme) {
          actions.toggleTheme()
        } else {
          log.warn(
            'Theme toggle not yet implemented - consider adding theme context or zustand store'
          )
        }
      },
    },

    // Help Commands
    {
      id: 'help-shortcuts',
      label: 'Show Keyboard Shortcuts',
      description: 'View all available keyboard shortcuts',
      keywords: ['help', 'shortcuts', 'keyboard', 'keys'],
      shortcut: 'Ctrl+/',
      category: 'Help',
      icon: '⌨️',
      execute: () => {
        if (actions.openShortcutsHelp) {
          actions.openShortcutsHelp()
        } else {
          log.warn(
            'Shortcuts Help action not implemented - please pass openShortcutsHelp handler to createCommandsRegistry'
          )
        }
      },
    },
    {
      id: 'help-command-palette',
      label: 'Open Command Palette',
      description: 'Show this command palette',
      keywords: ['command', 'palette', 'search'],
      shortcut: 'Ctrl+K',
      category: 'Help',
      icon: '⌘',
      execute: () => {
        if (actions.openCommandPalette) {
          actions.openCommandPalette()
        } else {
          log.warn(
            'Command Palette action not implemented - please pass openCommandPalette handler to createCommandsRegistry'
          )
        }
      },
    },
  ]

  return commands
}

/**
 * Get all unique categories from commands
 */
export function getCommandCategories(commands: Command[]): CommandCategory[] {
  const categories = new Set<CommandCategory>()
  commands.forEach((cmd) => categories.add(cmd.category))
  return Array.from(categories).sort()
}

/**
 * Filter commands by category
 */
export function getCommandsByCategory(
  commands: Command[],
  category: CommandCategory
): Command[] {
  return commands.filter((cmd) => cmd.category === category)
}
